import React, { useMemo } from 'react';
import { compactNumber, type DayPoint, type RankedSlice } from './usageChartData';

/**
 * Графики раздела Usage & Billing.
 *
 * Рисуются вручную на SVG, без библиотеки: в проекте уже есть chart.js, но ради
 * двух простых форм тянуть его на экран, который и так грузится одним куском на
 * 2,6 МБ, незачем. Оба графика строятся по загруженной странице — подпись об
 * этом обязана быть на самом блоке, иначе столбик прочитают как итог периода.
 */

const fmtInt = (value: number): string => Math.round(value).toLocaleString('en-US');
const fmtCost = (value: number): string => (value > 0 && value < 1 ? `$${value.toFixed(4)}` : `$${value.toFixed(2)}`);

interface ChartCardProps {
  title: string;
  hint?: string;
  children: React.ReactNode;
}

export const ChartCard: React.FC<ChartCardProps> = ({ title, hint, children }) => (
  <section className="ub-chart">
    <header className="ub-chart__head">
      <h3 className="ub-chart__title">{title}</h3>
      {hint ? <span className="ub-chart__hint">{hint}</span> : null}
    </header>
    {children}
  </section>
);

/* ─── Ранжированные полосы ────────────────────────────────────────────────── */

export const RankedBars: React.FC<{ slices: RankedSlice[]; emptyText: string }> = ({ slices, emptyText }) => {
  const max = useMemo(() => slices.reduce((m, s) => Math.max(m, s.total), 0), [slices]);

  if (slices.length === 0 || max <= 0) {
    return <p className="ub-chart__empty">{emptyText}</p>;
  }

  const hasCached = slices.some((slice) => slice.cached > 0);

  return (
    <>
      <ul className="ub-bars">
        {slices.map((slice) => {
        const width = (slice.total / max) * 100;
        // Кэш — часть той же полосы, а не отдельная: это не дополнительный
        // расход, а доля уже посчитанных токенов, отданных из кэша.
        const cachedWidth = slice.total > 0 ? Math.min(100, (slice.cached / slice.total) * 100) : 0;
        return (
          <li key={slice.key} className={`ub-bar-row${slice.key === '__other__' ? ' ub-bar-row--other' : ''}`}>
            <span className="ub-bar-row__label" title={slice.label}>
              {slice.label}
            </span>
            <span className="ub-bar-row__track">
              <span
                className="ub-bar-row__fill"
                style={{ width: `${Math.max(width, 1.5)}%` }}
                title={`${fmtInt(slice.total)} tokens · ${fmtCost(slice.cost)} · ${fmtInt(slice.records)} records`}
              >
                {cachedWidth > 0 ? (
                  <span
                    className="ub-bar-row__cached"
                    style={{ width: `${cachedWidth}%` }}
                    title={`${fmtInt(slice.cached)} cached tokens`}
                  />
                ) : null}
              </span>
            </span>
            <span className="ub-bar-row__value">{compactNumber(slice.total)}</span>
            <span className="ub-bar-row__share">{`${Math.round(slice.share * 100)}%`}</span>
          </li>
        );
      })}
      </ul>
      {/* Светлый отрезок внутри полосы ничем не подписан, а читается как отдельная
          величина — легенда появляется только когда кэш вообще есть. */}
      {hasCached ? (
        <p className="ub-chart__legend">
          <span className="ub-chart__swatch" aria-hidden="true" />
          lighter part of each bar — tokens served from cache
        </p>
      ) : null}
    </>
  );
};

/* ─── Расход по дням ──────────────────────────────────────────────────────── */

const CHART_H = 132;
const PAD_TOP = 10;
const PAD_BOTTOM = 20;

export const DailyBars: React.FC<{ points: DayPoint[]; emptyText: string }> = ({ points, emptyText }) => {
  const max = useMemo(() => points.reduce((m, p) => Math.max(m, p.total), 0), [points]);

  if (points.length === 0 || max <= 0) {
    return <p className="ub-chart__empty">{emptyText}</p>;
  }

  const plotH = CHART_H - PAD_TOP - PAD_BOTTOM;
  const slot = 100 / points.length;
  // Узкие столбики при коротком ряде выглядят как случайные штрихи, а при
  // длинном сливаются — ширина считается от числа дней, а не задаётся числом.
  const barW = Math.min(slot * 0.62, 3.6);
  const peak = points.reduce((best, p) => (p.total > best.total ? p : best), points[0]);

  return (
    <div className="ub-daily">
      <svg
        className="ub-daily__svg"
        viewBox={`0 0 100 ${CHART_H}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={`Spend by day, peak ${fmtInt(peak.total)} tokens on ${peak.day}`}
      >
        {[0.25, 0.5, 0.75].map((f) => (
          <line
            key={f}
            x1="0"
            x2="100"
            y1={PAD_TOP + plotH * f}
            y2={PAD_TOP + plotH * f}
            className="ub-daily__grid"
            vectorEffect="non-scaling-stroke"
          />
        ))}

        {points.map((point, i) => {
          const h = max > 0 ? (point.total / max) * plotH : 0;
          const x = slot * i + (slot - barW) / 2;
          const isPeak = point.day === peak.day && point.total > 0;
          return (
            <rect
              key={point.day}
              x={x}
              y={PAD_TOP + plotH - h}
              width={barW}
              height={Math.max(h, point.total > 0 ? 1.5 : 0)}
              rx="0.6"
              className={`ub-daily__bar${isPeak ? ' ub-daily__bar--peak' : ''}`}
            >
              <title>{`${point.day} · ${fmtInt(point.total)} tokens · ${fmtCost(point.cost)}`}</title>
            </rect>
          );
        })}

        <line
          x1="0"
          x2="100"
          y1={PAD_TOP + plotH}
          y2={PAD_TOP + plotH}
          className="ub-daily__axis"
          vectorEffect="non-scaling-stroke"
        />
      </svg>

      <div className="ub-daily__legend">
        <span>{points[0].day}</span>
        <span className="ub-daily__peak" title={`${fmtInt(peak.total)} tokens`}>
          {`peak ${compactNumber(peak.total)} on ${peak.day}`}
        </span>
        <span>{points[points.length - 1].day}</span>
      </div>
    </div>
  );
};
